import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import fileDownload from "js-file-download";
import { Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../../components/PageHero";
import FilterBand from "../../../../components/FilterBand";
import MetricCard from "../../../../components/MetricCard";
import MetricCardGroup from "../../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../../components/ShellCard";
import SortHeader from "../../../../components/SortHeader";
import TablePagination from "../../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { sortRows, toggleSort, paginate } from "../../../../utils/tableHelpers";
import { formatElapsedHours, faixaLabel } from "../../../../utils/formatElapsed";
import { ETAPAS_UI, ETAPAS_ORDEM } from "./etapas";
import "./styles.css";

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
  { key: "180d", label: "180 dias", days: 179 },
];

const getQuickRangeDates = (rangeKey) => {
  const today = moment();
  if (rangeKey === "month") {
    return {
      desde: today.clone().startOf("month").format("YYYY-MM-DD"),
      ate: today.format("YYYY-MM-DD"),
    };
  }
  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range && range.days != null ? range.days : 29;
  return {
    desde: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
    ate: today.format("YYYY-MM-DD"),
  };
};

const createDefaultPeriodo = () => getQuickRangeDates("month");

const PAGE_SIZES = [10, 20, 50, 100];

const fmtBRL = (v) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      });

const fmtPct = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const fmtDataHora = (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "-");

const badgeToneClass = (tone) => `ff-badge ff-badge-${tone || "neutral"}`;

// Filtro de texto instantâneo (client-side) sobre ORC/BR, cliente e
// transportadora do recorte já carregado.
function matchesSearch(item, termo) {
  if (!termo) return true;
  const alvo = termo.toLowerCase();
  const campos = [item.baseNumEmbarque, item.numEmbarque, item.cliente, item.transportadora];
  return campos.some((c) => c && String(c).toLowerCase().indexOf(alvo) !== -1);
}

const FilaFretesGoBID = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const history = useHistory();
  const empresaId = empresa && empresa.value;

  const [periodo, setPeriodo] = useState(createDefaultPeriodo);
  const [periodoDraft, setPeriodoDraft] = useState(createDefaultPeriodo);
  const periodoRef = useRef(periodo);
  useEffect(() => {
    periodoRef.current = periodo;
  }, [periodo]);

  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [etapaFiltro, setEtapaFiltro] = useState(null);
  const [transportadoraFiltro, setTransportadoraFiltro] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState({ field: "tempoEtapaHoras", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [cardsData, setCardsData] = useState(null);
  const [abertosData, setAbertosData] = useState(null);
  const [expedidoData, setExpedidoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);

  const fetchCards = useCallback(
    async (periodoAtual) => {
      const res = await api.get("/indicadores/gobid/fila/cards", {
        params: { idEmbarcador: empresaId, desde: periodoAtual.desde, ate: periodoAtual.ate },
      });
      return res.data;
    },
    [empresaId],
  );

  const fetchAbertos = useCallback(
    async (periodoAtual) => {
      const res = await api.get("/indicadores/gobid/fila/lista", {
        params: { idEmbarcador: empresaId, desde: periodoAtual.desde, ate: periodoAtual.ate, limit: 2000 },
      });
      return res.data;
    },
    [empresaId],
  );

  const fetchExpedido = useCallback(
    async (periodoAtual) => {
      const res = await api.get("/indicadores/gobid/fila/lista", {
        params: {
          idEmbarcador: empresaId,
          desde: periodoAtual.desde,
          ate: periodoAtual.ate,
          etapa: "expedido",
          limit: 2000,
        },
      });
      return res.data;
    },
    [empresaId],
  );

  const loadDashboard = useCallback(
    async (periodoAtual, precisaExpedido) => {
      if (!empresaId || !hasEmpresaSelection) return;
      setLoading(true);
      setError(null);
      try {
        const [cards, abertos] = await Promise.all([
          fetchCards(periodoAtual),
          fetchAbertos(periodoAtual),
        ]);
        setCardsData(cards);
        setAbertosData(abertos);
        if (precisaExpedido) {
          setListLoading(true);
          const expedido = await fetchExpedido(periodoAtual);
          setExpedidoData(expedido);
        } else {
          setExpedidoData(null);
        }
        setConsultado(true);
      } catch (requestError) {
        const msg =
          (requestError.response && requestError.response.data && requestError.response.data.error) ||
          "Erro ao carregar a fila de fretes.";
        setError(msg);
        setConsultado(true);
        addToast({
          place: "tr",
          type: "danger",
          title: "Fila de Fretes",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
        setListLoading(false);
      }
    },
    [empresaId, hasEmpresaSelection, addToast, fetchCards, fetchAbertos, fetchExpedido],
  );

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setCardsData(null);
      setAbertosData(null);
      setExpedidoData(null);
      setError(null);
      setConsultado(false);
      return;
    }
    loadDashboard(periodoRef.current, etapaFiltro === "expedido");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, hasEmpresaSelection]);

  const applyQuickRange = (key) => {
    const next = getQuickRangeDates(key);
    setPeriodoDraft(next);
    setPeriodo(next);
    setPage(1);
    loadDashboard(next, etapaFiltro === "expedido");
  };

  const handleApplyPeriodo = () => {
    setPeriodo(periodoDraft);
    setPage(1);
    loadDashboard(periodoDraft, etapaFiltro === "expedido");
  };

  const handleClearFiltros = () => {
    const defaultPeriodo = createDefaultPeriodo();
    setPeriodoDraft(defaultPeriodo);
    setPeriodo(defaultPeriodo);
    setEtapaFiltro(null);
    setTransportadoraFiltro(null);
    setSearchTerm("");
    setPage(1);
    loadDashboard(defaultPeriodo, false);
  };

  const handleToggleEtapa = async (key) => {
    const next = etapaFiltro === key ? null : key;
    setEtapaFiltro(next);
    setPage(1);
    if (next === "expedido" && !expedidoData) {
      setListLoading(true);
      try {
        const expedido = await fetchExpedido(periodo);
        setExpedidoData(expedido);
      } catch (requestError) {
        addToast({
          place: "tr",
          type: "danger",
          title: "Fila de Fretes",
          description: "Falha ao carregar os embarques expedidos.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setListLoading(false);
      }
    }
  };

  const handleExport = async () => {
    if (!empresaId) return;
    setExportLoading(true);
    try {
      const params = {
        idEmbarcador: empresaId,
        desde: periodo.desde,
        ate: periodo.ate,
        limit: 5000,
      };
      if (etapaFiltro) params.etapa = etapaFiltro;
      if (transportadoraFiltro) params.transportadora = transportadoraFiltro;
      const res = await api.get("/indicadores/gobid/fila/lista/export", {
        params,
        responseType: "blob",
      });
      fileDownload(res.data, `gobid-fila-fretes-${periodo.desde}-a-${periodo.ate}.xlsx`);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Exportar Excel",
        description: "Falha ao gerar a planilha.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleSort = (field) => setSort((current) => toggleSort(current, field));

  // Todos os Hooks (useMemo incluído) precisam ser chamados antes de qualquer
  // retorno condicional — por isso a derivação dos itens e o gate de
  // embarcador (abaixo) vêm depois destes.
  const itensAbertosBase = (abertosData && abertosData.itens) || [];
  const itensExpedidoBase = (expedidoData && expedidoData.itens) || [];
  const modoExpedido = etapaFiltro === "expedido";
  const fonteAtual = modoExpedido ? expedidoData : abertosData;
  const itensBase = modoExpedido ? itensExpedidoBase : itensAbertosBase;

  const transportadoraOptions = useMemo(() => {
    const nomes = new Set();
    itensAbertosBase.forEach((i) => i.transportadora && nomes.add(i.transportadora));
    itensExpedidoBase.forEach((i) => i.transportadora && nomes.add(i.transportadora));
    return Array.from(nomes).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [itensAbertosBase, itensExpedidoBase]);

  const itensFiltrados = useMemo(() => {
    return itensBase.filter((item) => {
      if (!modoExpedido && etapaFiltro && item.etapa !== etapaFiltro) return false;
      if (transportadoraFiltro && item.transportadora !== transportadoraFiltro) return false;
      return matchesSearch(item, searchTerm);
    });
  }, [itensBase, modoExpedido, etapaFiltro, transportadoraFiltro, searchTerm]);

  const itensOrdenados = useMemo(() => sortRows(itensFiltrados, sort), [itensFiltrados, sort]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content ff-page">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Este dashboard usa o embarcador selecionado no topo para montar a fila de fretes do GoBID."
        />
      </div>
    );
  }

  const paginaAtual = paginate(itensOrdenados, page, pageSize);
  const limitadoNaOrigem = fonteAtual ? fonteAtual.limitado : false;

  const periodoLabel = `${moment(periodo.desde).format("DD/MM/YYYY")} a ${moment(periodo.ate).format(
    "DD/MM/YYYY",
  )}`;
  const activeRange = QUICK_RANGES.find((range) => {
    const r = getQuickRangeDates(range.key);
    return r.desde === periodoDraft.desde && r.ate === periodoDraft.ate;
  });

  const activeFiltersCount =
    (etapaFiltro ? 1 : 0) +
    (transportadoraFiltro ? 1 : 0) +
    (periodo.desde !== periodoDraft.desde || periodo.ate !== periodoDraft.ate ? 1 : 0);

  const etapasCards = (cardsData && cardsData.etapas) || [];

  const columns = [
    { field: "baseNumEmbarque", label: "ORC/BR" },
    { field: "criadoEm", label: "Criado em" },
    { field: "cliente", label: "Cliente" },
    { field: "regiao", label: "Região" },
    { field: "valorNF", label: "Valor NF", numeric: true },
    { field: "frete", label: "Frete", numeric: true },
    { field: "pctFreteNota", label: "% Frete/NF", numeric: true },
    { field: "transportadora", label: "Transportadora" },
    { field: "etapa", label: "Etapa" },
    { field: "tempoEtapaHoras", label: "Tempo na etapa", numeric: true },
  ];

  return (
    <div className="content ff-page">
      <PageHero
        eyebrow="GoBID · Operação"
        title="Fila de Fretes"
        description="Onde cada cotação está e há quanto tempo, do orçamento à expedição."
        actions={
          <>
            <PageHeroRefreshButton
              onClick={() => loadDashboard(periodo, modoExpedido)}
              loading={loading}
            />
            <PageHeroExportButton
              label="Exportar Excel"
              onClick={handleExport}
              loading={exportLoading}
              disabled={itensOrdenados.length === 0}
            />
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel="Buscar"
        searchPlaceholder="ORC/BR, cliente ou transportadora..."
        searchValue={searchTerm}
        onSearchChange={(v) => {
          setSearchTerm(v);
          setPage(1);
        }}
        activeFiltersCount={activeFiltersCount}
        open={mostrarFiltros}
        onToggle={setMostrarFiltros}
        panelTitle="Filtros"
        panelDescription={`Período: ${periodoLabel}${!activeRange ? " (personalizado)" : ""}`}
        showActions
        busy={loading}
        onApply={handleApplyPeriodo}
        onClear={handleClearFiltros}
      >
        <div className="ff-quick-ranges">
          {QUICK_RANGES.map((range) => (
            <button
              key={range.key}
              type="button"
              className={`ff-quick-range${activeRange && activeRange.key === range.key ? " is-active" : ""}`}
              disabled={loading}
              onClick={() => applyQuickRange(range.key)}
            >
              {range.label}
            </button>
          ))}
        </div>
        <Row className="ff-filters-row">
          <Col>
            <FormGroup>
              <Label>De</Label>
              <Input
                type="date"
                value={periodoDraft.desde}
                disabled={loading}
                onChange={(e) => { const valor = e.target.value; setPeriodoDraft((cur) => ({ ...cur, desde: valor })); }}
              />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label>Até</Label>
              <Input
                type="date"
                value={periodoDraft.ate}
                disabled={loading}
                onChange={(e) => { const valor = e.target.value; setPeriodoDraft((cur) => ({ ...cur, ate: valor })); }}
              />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label>Etapa</Label>
              <Input
                type="select"
                value={etapaFiltro || ""}
                onChange={(e) => handleToggleEtapa(e.target.value || null)}
              >
                <option value="">Todas em aberto</option>
                {ETAPAS_ORDEM.map((key) => (
                  <option key={key} value={key}>
                    {ETAPAS_UI[key].label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label>Transportadora</Label>
              <Input
                type="select"
                value={transportadoraFiltro || ""}
                onChange={(e) => {
                  setTransportadoraFiltro(e.target.value || null);
                  setPage(1);
                }}
              >
                <option value="">Todas</option>
                {transportadoraOptions.map((nome) => (
                  <option key={nome} value={nome}>
                    {nome}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup
        title="Etapas da fila"
        description="Clique numa etapa para filtrar a lista; clique de novo para desfazer."
      >
        {ETAPAS_ORDEM.map((key) => {
          const info = ETAPAS_UI[key];
          const card = etapasCards.find((e) => e.etapa === key);
          const qtd = card ? card.qtd : 0;
          const helper = card && card.aberta
            ? `tempo médio ${formatElapsedHours(card.ageingMedioHoras)} · ${
                card.faixas ? card.faixas.acima3d : 0
              } acima de 3 dias`
            : `no período ${periodoLabel}`;

          return (
            <MetricCard
              key={key}
              label={info.label}
              value={String(qtd)}
              helper={helper}
              iconClassName={info.icon}
              tone={info.tone}
              active={etapaFiltro === key}
              onClick={() => handleToggleEtapa(key)}
            />
          );
        })}
      </MetricCardGroup>

      <ShellCard
        title="Cotações na fila"
        description={`${paginaAtual.total} resultado(s)${
          limitadoNaOrigem ? ", mostrando as primeiras 2000 cotações do período" : ""
        }`}
      >
        <TableSurface>
          {error ? (
            <div className="ff-empty-state">{error}</div>
          ) : loading ? (
            <div className="ff-empty-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Carregando fila de fretes...
            </div>
          ) : !consultado ? (
            <div className="ff-empty-state">Selecione um período e consulte.</div>
          ) : listLoading ? (
            <div className="ff-empty-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Carregando expedidos do
              período...
            </div>
          ) : itensOrdenados.length === 0 ? (
            <div className="ff-empty-state">Nenhuma cotação encontrada para os filtros informados.</div>
          ) : (
            <Table className="ff-table hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  {columns.map((col) => (
                    <th key={col.field} className={col.numeric ? "is-number" : undefined}>
                      <SortHeader
                        label={col.label}
                        field={col.field}
                        sort={sort}
                        onSort={handleSort}
                        align={col.numeric ? "right" : "left"}
                      />
                    </th>
                  ))}
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {paginaAtual.items.map((item) => (
                  <tr key={item._id}>
                    <td>{item.baseNumEmbarque || "-"}</td>
                    <td>{fmtDataHora(item.criadoEm)}</td>
                    <td>{item.cliente || "-"}</td>
                    <td>{[item.cidadeDestino, item.ufDestino].filter(Boolean).join("/") || "-"}</td>
                    <td className="is-number">{fmtBRL(item.valorNF)}</td>
                    <td className="is-number">
                      {fmtBRL(item.frete)}
                      {item.freteEstimado ? <span className="ff-pill-estimado">estimado</span> : null}
                    </td>
                    <td className="is-number">{fmtPct(item.pctFreteNota)}</td>
                    <td>{item.transportadora || "-"}</td>
                    <td>
                      <span className={badgeToneClass(ETAPAS_UI[item.etapa] && ETAPAS_UI[item.etapa].tone)}>
                        {item.etapaLabel}
                      </span>
                    </td>
                    <td className="is-number">
                      {item.faixa ? (
                        <span className={`ff-badge ff-faixa-${item.faixa}`} title={faixaLabel(item.faixa)}>
                          {formatElapsedHours(item.tempoEtapaHoras)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ff-btn-abrir"
                        onClick={() =>
                          history.push(`/app/gobid/operacao/listagem-cotacoes?abrir=${item._id}`)
                        }
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {!error && !loading && !listLoading && consultado && itensOrdenados.length > 0 ? (
            <TablePagination
              page={paginaAtual.page}
              pageSize={pageSize}
              total={paginaAtual.total}
              pageSizes={PAGE_SIZES}
              itemLabel="cotação(ões)"
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          ) : null}
        </TableSurface>
      </ShellCard>
    </div>
  );
};

export default FilaFretesGoBID;
